import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PhoneInput } from "@/components/PhoneInput";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { ArrowLeft, ArrowRight, Scissors, Loader2, Gift } from "lucide-react";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1552710307-537199cd41c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxBZnJpY2FuJTIwdGFpbG9yJTIwc2V3aW5nJTIwY29sb3JmdWwlMjBmYWJyaWN8ZW58MHx8fHwxNzg0MDM4MzQyfDA&ixlib=rb-4.1.0&q=85&w=800";

export default function Auth() {
  const { login, register, resetPin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("landing"); // landing | login | register | forgot
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [phone, setPhone] = useState("+221");
  const [pin, setPin] = useState("");
  const [nom, setNom] = useState("");
  const [atelier, setAtelier] = useState("");
  const [ville, setVille] = useState("");

  const reset = () => {
    setPin("");
  };
  const back = () => {
    reset();
    if (step > 0) setStep((s) => s - 1);
    else setMode("landing");
  };

  const goLogin = () => { setMode("login"); setStep(0); setPin(""); };
  const goRegister = () => { setMode("register"); setStep(0); setPin(""); };
  const goForgot = () => { setMode("forgot"); setStep(0); setPin(""); };

  const validPhone = /^\+\d{7,15}$/.test(phone);
  const refCode = typeof window !== "undefined" ? localStorage.getItem("tp_ref") : null;

  async function nextFromPhone() {
    if (!validPhone) return toast.error("Entrez un numéro valide");
    if (mode === "login" || mode === "forgot") {
      setStep(1);
      return;
    }
    // register: check phone not taken
    setLoading(true);
    try {
      const { data } = await api.post("/auth/check-phone", { telephone: phone, pin: "0000" });
      if (data.exists) {
        toast.error("Ce numéro est déjà inscrit. Connectez-vous.");
        setMode("login");
        setStep(1);
      } else {
        setStep(1);
      }
    } catch (e) {
      setStep(1);
    } finally {
      setLoading(false);
    }
  }

  async function doLogin() {
    setLoading(true);
    try {
      await login({ telephone: phone, pin });
      localStorage.setItem("tp_onboarded", "1");
      toast.success("Bienvenue !");
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
      setPin("");
    } finally {
      setLoading(false);
    }
  }

  async function doRegister() {
    setLoading(true);
    try {
      await register({ telephone: phone, pin, nom, atelier, ville, ref: localStorage.getItem("tp_ref") || undefined });
      localStorage.removeItem("tp_ref");
      localStorage.setItem("tp_onboarded", "0");
      toast.success("Compte créé !");
      navigate("/onboarding");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  async function doForgot() {
    setLoading(true);
    try {
      await resetPin({ telephone: phone, new_pin: pin });
      localStorage.setItem("tp_onboarded", "1");
      toast.success("Nouveau code PIN enregistré !");
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
      setPin("");
    } finally {
      setLoading(false);
    }
  }

  // ---- Landing ----
  if (mode === "landing") {
    return (
      <div className="min-h-screen flex flex-col bg-tp-green text-white">
        <div className="relative h-[42vh] overflow-hidden">
          <img src={LOGIN_BG} alt="Tailleur au travail" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-tp-green via-tp-green/60 to-transparent" />
        </div>
        <div className="flex-1 px-6 pb-10 -mt-10 relative flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-11 h-11 rounded-2xl bg-tp-gold flex items-center justify-center">
              <Scissors size={22} className="text-tp-ink" />
            </div>
            <span className="font-head text-2xl font-extrabold">Tailleur Pro</span>
          </div>
          <h1 className="font-head text-3xl font-extrabold leading-tight">
            Vos mesures,<br />toujours dans la poche.
          </h1>
          <p className="mt-3 text-white/80 text-base leading-relaxed">
            Enregistrez vos clients, leurs mesures et leurs tissus. Retrouvez tout en 2 secondes, même sans internet.
          </p>
          <div className="mt-auto pt-8 space-y-3">
            <Button
              data-testid="landing-register-btn"
              onClick={goRegister}
              className="w-full h-[54px] rounded-full bg-tp-gold hover:bg-tp-gold/90 text-tp-ink text-base font-bold"
            >
              Créer mon compte <ArrowRight size={20} />
            </Button>
            <button
              data-testid="landing-login-btn"
              onClick={goLogin}
              className="w-full h-[54px] rounded-full border border-white/40 text-white text-base font-semibold active:scale-[0.98] transition-transform"
            >
              J'ai déjà un compte
            </button>
          </div>
        </div>
      </div>
    );
  }

  const titles = {
    login: "Connexion",
    register: "Créer un compte",
    forgot: "Code PIN oublié",
  };

  return (
    <div className="min-h-screen bg-tp-bg flex flex-col">
      <div className="px-4 pt-5 pb-2 flex items-center gap-3">
        <button data-testid="auth-back-btn" onClick={back} className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-gray-200">
          <ArrowLeft size={20} className="text-tp-ink" />
        </button>
        <h2 className="font-head text-xl font-bold text-tp-ink">{titles[mode]}</h2>
      </div>

      <div className="flex-1 px-6 pt-4">
        {mode === "register" && refCode && (
          <div data-testid="ref-bonus-banner" className="mb-4 flex items-center gap-2 bg-tp-green/10 border border-tp-green/30 rounded-xl px-4 py-3">
            <Gift size={20} className="text-tp-green shrink-0" />
            <p className="text-sm text-tp-ink font-semibold">
              Code parrain <span className="text-tp-green">{refCode}</span> appliqué : <b>+5 clients offerts</b> à l'inscription 🎁
            </p>
          </div>
        )}
        <AnimatePresence mode="wait">
          {/* STEP 0: phone */}
          {step === 0 && (
            <motion.div key="phone" className="tp-fade-up space-y-5">
              <div>
                <label className="font-head font-semibold text-tp-ink block mb-2">Numéro de téléphone</label>
                <PhoneInput value={phone} onChange={setPhone} autoFocus />
                <p className="text-sm text-tp-muted mt-2">Ce numéro sera votre identifiant de connexion.</p>
              </div>
              <Button
                data-testid="phone-next-btn"
                disabled={!validPhone || loading}
                onClick={nextFromPhone}
                className="w-full h-[54px] rounded-full bg-tp-green hover:bg-tp-greenDark text-base font-bold"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : <>Continuer <ArrowRight size={20} /></>}
              </Button>
              {mode === "login" && (
                <button data-testid="goto-forgot-btn" onClick={goForgot} className="w-full text-center text-tp-green font-semibold text-base py-2">
                  Code PIN oublié ?
                </button>
              )}
            </motion.div>
          )}

          {/* REGISTER STEP 1: details */}
          {mode === "register" && step === 1 && (
            <motion.div key="details" className="tp-fade-up space-y-4">
              <Field label="Nom complet *" testId="reg-nom" value={nom} onChange={setNom} placeholder="Ex: Moussa Diop" autoFocus />
              <Field label="Nom de l'atelier" testId="reg-atelier" value={atelier} onChange={setAtelier} placeholder="Ex: Ndiaye Couture" />
              <Field label="Ville" testId="reg-ville" value={ville} onChange={setVille} placeholder="Ex: Dakar" />
              <Button
                data-testid="details-next-btn"
                disabled={!nom.trim()}
                onClick={() => setStep(2)}
                className="w-full h-[54px] rounded-full bg-tp-green hover:bg-tp-greenDark text-base font-bold"
              >
                Continuer <ArrowRight size={20} />
              </Button>
            </motion.div>
          )}

          {/* PIN step (login step1, register step2, forgot step1) */}
          {((mode === "login" && step === 1) ||
            (mode === "register" && step === 2) ||
            (mode === "forgot" && step === 1)) && (
            <motion.div key="pin" className="tp-fade-up flex flex-col items-center pt-4">
              <p className="font-head font-semibold text-tp-ink text-lg mb-1">
                {mode === "login" ? "Entrez votre code PIN" : "Choisissez un code PIN"}
              </p>
              <p className="text-sm text-tp-muted mb-6 text-center">4 chiffres faciles à retenir</p>
              <InputOTP
                maxLength={4}
                value={pin}
                onChange={setPin}
                inputMode="numeric"
                data-testid="pin-input"
                autoFocus
              >
                <InputOTPGroup className="gap-3">
                  {[0, 1, 2, 3].map((i) => (
                    <InputOTPSlot
                      key={i}
                      index={i}
                      className="w-14 h-16 text-3xl font-bold rounded-xl border-2 bg-white"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              <Button
                data-testid="pin-submit-btn"
                disabled={pin.length !== 4 || loading}
                onClick={mode === "login" ? doLogin : mode === "register" ? doRegister : doForgot}
                className="w-full h-[54px] rounded-full bg-tp-green hover:bg-tp-greenDark text-base font-bold mt-8"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : mode === "login" ? "Se connecter" : "Valider"}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId, autoFocus }) {
  return (
    <div>
      <label className="font-head font-semibold text-tp-ink block mb-2">{label}</label>
      <input
        data-testid={testId}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-[52px] rounded-xl bg-white border border-gray-300 px-4 text-base outline-none focus:border-tp-green focus:ring-2 focus:ring-tp-green/20"
      />
    </div>
  );
}
